import React, { useState, useRef } from "react"
import {
  DioboxEvent, Speaker, AgendaRow,
} from "sharedTypes"
import {
  Box, Flex, useDisclosure, HStack,
} from "@chakra-ui/react"
import EventPage, { EventPageHeader } from "components/layout/EventPage"
import ViewsCount from "components/elements/ViewsCount"
import { useForms } from "queries/forms"
import { eventWebPagePath } from "utilities/routes"
import SiteCover from "components/Portal/website/SiteCover"
import EventDetail from "components/Portal/website/event_detail/EventDetail"
import Speakers from "components/Portal/website/Speakers"
import Agenda from "components/Portal/website/Agenda"
import GoogleMap from "components/Portal/website/GoogleMap"
import PoweredBy from "components/Portal/website/PoweredBy"
import WebsitePreviewSection from "components/Portal/website/WebsitePreviewSection"
import HeroTemplate from "modals/HeroTemplate"
import HeroImage from "modals/HeroImage"
import PageHeader from "modals/PageHeader"
import ActionButton from "modals/ActionButton"
import WebpageBuilder from "modals/WebpageBuilder"
import Sidebar from "modals/Sidebar"
import SpeakerSettings from "modals/SpeakerSettings"
import SpeakerForm from "pages/EventShow/WebPage/SpeakerForm"
import AgendaSettings from "modals/AgendaSettings"
import AddAgendaGroup from "modals/AddAgendaGroup"
import AddAgendaEntry from "modals/AddAgendaEntry"
import MapSettings from "modals/MapSettings"
import PublishedUpdater from "./PublishedUpdater"
import PageLinkSelector from "./PageLinkSelector"
import TopBar from "../Forms/StyleAndConfiguration/PortalPreview/PreviewFrameWithBranding/TopBar"
import useWebsitePreview from "./hooks/useWebsitePreview"
import {
  WebsiteFormValues,
  SpeakerFormValues,
  AgendaFormValues,
  RegistrationButtonFormValues,
  HeroImageFormValues,
} from "./types"

type Props = {
  event: DioboxEvent
}

const WebPageSettings = ({
  event,
}: Props) => {
  const {
    website,
    website: {
      viewsCount,
      websiteData: { blocks: websiteBlocks },
    },
    id: eventId,
  } = event

  const [editingSpeaker, setEditingSpeaker] = useState<Speaker | null>(null)
  const [editingAgendaItem, setEditingAgendaItem] = useState<AgendaRow | null>(null)
  const [editingAgendaGroup, setEditingAgendaGroup] = useState<AgendaRow | null>(null)
  const tempSpeakerIdRef = useRef<number | null>(null)
  const tempAgendaItemIdRef = useRef<number | null>(null)
  const tempAgendaGroupIdRef = useRef<number | null>(null)

  const {
    previewWebsite,
    updatePreview,
    updateSpeakerPreview,
    updateAgendaRowPreview,
    resetPreview,
  } = useWebsitePreview(website)

  const { data: forms } = useForms(eventId)
  const handleRegistrationButtonClick = previewWebsite.actionBehavior
    ? () => {
      const url = ["forms", "tickets", "waitlist"].includes(previewWebsite.actionBehavior!)
        ? event.urls?.portal
        : forms?.data.find(({ externalId }) => externalId === previewWebsite.actionBehavior)?.url
      if (url) {
        window.open(url, "_blank")
      }
    }
    : null

  const {
    isOpen: isHeroTemplateOpen,
    onOpen: onHeroTemplateOpen,
    onClose: onHeroTemplateClose,
  } = useDisclosure()
  const {
    isOpen: isHeroImageOpen,
    onOpen: onHeroImageOpen,
    onClose: onHeroImageClose,
  } = useDisclosure()
  const {
    isOpen: isPageHeaderOpen,
    onOpen: onPageHeaderOpen,
    onClose: onPageHeaderClose,
  } = useDisclosure()
  const {
    isOpen: isRegistrationButtonOpen,
    onOpen: onRegistrationButtonOpen,
    onClose: onRegistrationButtonClose,
  } = useDisclosure()
  const {
    isOpen: isWebpageBuilderOpen,
    onOpen: onWebpageBuilderOpen,
    onClose: onWebpageBuilderClose,
  } = useDisclosure()
  const {
    isOpen: isSidebarOpen,
    onOpen: onSidebarOpen,
    onClose: onSidebarClose,
  } = useDisclosure()
  const {
    isOpen: isSpeakerSettingsOpen,
    onOpen: onSpeakerSettingsOpen,
    onClose: onSpeakerSettingsClose,
  } = useDisclosure()
  const {
    isOpen: isAddSpeakerOpen,
    onOpen: onAddSpeakerOpen,
    onClose: onAddSpeakerClose,
  } = useDisclosure()
  const {
    isOpen: isAgendaSettingsOpen,
    onOpen: onAgendaSettingsOpen,
    onClose: onAgendaSettingsClose,
  } = useDisclosure()
  const {
    isOpen: isAddAgendaGroupOpen,
    onOpen: onAddAgendaGroupOpen,
    onClose: onAddAgendaGroupClose,
  } = useDisclosure()
  const {
    isOpen: isAddAgendaEntryOpen,
    onOpen: onAddAgendaEntryOpen,
    onClose: onAddAgendaEntryClose,
  } = useDisclosure()
  const {
    isOpen: isMapSettingsOpen,
    onOpen: onMapSettingsOpen,
    onClose: onMapSettingsClose,
  } = useDisclosure()

  // Helper to create simple change handlers that merge values into preview
  const createChangeHandler = (values: WebsiteFormValues) => {
    updatePreview(values)
  }

  // Helper to create cancel handlers that reset preview and close modal
  // This is only called when cancel button is clicked, not on submit
  const createCancelHandler = (onClose: () => void) => () => {
    resetPreview()
    onClose()
  }

  // Helper to create submit handlers that close modal without resetting preview
  // This is called when form is successfully submitted
  const createSubmitHandler = (onClose: () => void) => () => {
    // Don't reset preview on submit - let useEffect sync when website prop updates
    onClose()
  }

  // Helper to create close handlers that also clean up editing state and temp refs
  const createFormCloseHandler = (
    setEditing: (value: null) => void,
    tempRef: React.MutableRefObject<number | null>,
    onClose: () => void,
  ) => () => {
    setEditing(null)
    // eslint-disable-next-line no-param-reassign
    tempRef.current = null
    resetPreview()
    onClose()
  }

  const handleSidebarChange = createChangeHandler
  const handleSidebarCancel = createCancelHandler(onSidebarClose)
  const handleSidebarSubmit = createSubmitHandler(onSidebarClose)

  const handleSpeakerSettingsChange = createChangeHandler
  const handleSpeakerSettingsCancel = createCancelHandler(onSpeakerSettingsClose)
  const handleSpeakerSettingsSubmit = createSubmitHandler(onSpeakerSettingsClose)

  const handleEditSpeaker = (speaker: Speaker) => {
    setEditingSpeaker(speaker)
  }

  const handleSpeakerFormClose = createFormCloseHandler(
    setEditingSpeaker,
    tempSpeakerIdRef,
    onAddSpeakerClose,
  )

  const handleAgendaSettingsChange = createChangeHandler
  const handleAgendaSettingsCancel = createCancelHandler(onAgendaSettingsClose)
  const handleAgendaSettingsSubmit = createSubmitHandler(onAgendaSettingsClose)

  const handleEditAgendaItem = (agendaItem: AgendaRow) => {
    setEditingAgendaItem(agendaItem)
  }

  const handleAgendaItemFormClose = createFormCloseHandler(
    setEditingAgendaItem,
    tempAgendaItemIdRef,
    onAddAgendaEntryClose,
  )

  const handleAgendaEntryChange = (values: AgendaFormValues) => {
    updateAgendaRowPreview(editingAgendaItem, tempAgendaItemIdRef, "AgendaItem", values)
  }

  const handleEditAgendaGroup = (agendaGroup: AgendaRow) => {
    setEditingAgendaGroup(agendaGroup)
  }

  const handleAgendaGroupChange = (values: AgendaFormValues) => {
    updateAgendaRowPreview(editingAgendaGroup, tempAgendaGroupIdRef, "AgendaHeading", values)
  }

  const handleAgendaGroupClose = createFormCloseHandler(
    setEditingAgendaGroup,
    tempAgendaGroupIdRef,
    onAddAgendaGroupClose,
  )

  const handleMapSettingsChange = createChangeHandler
  const handleMapSettingsCancel = createCancelHandler(onMapSettingsClose)
  const handleMapSettingsSubmit = createSubmitHandler(onMapSettingsClose)

  const handleHeroTemplateChange = createChangeHandler
  const handleHeroTemplateCancel = createCancelHandler(onHeroTemplateClose)
  const handleHeroTemplateSubmit = createSubmitHandler(onHeroTemplateClose)

  const handleHeroImageChange = (values: HeroImageFormValues) => {
    updatePreview(values as WebsiteFormValues)
  }
  const handleHeroImageCancel = createCancelHandler(onHeroImageClose)
  const handleHeroImageSubmit = createSubmitHandler(onHeroImageClose)

  const handlePageHeaderChange = createChangeHandler
  const handlePageHeaderCancel = createCancelHandler(onPageHeaderClose)
  const handlePageHeaderSubmit = createSubmitHandler(onPageHeaderClose)

  const handleRegistrationButtonChange = (values: RegistrationButtonFormValues) => {
    updatePreview(values as WebsiteFormValues)
  }
  const handleRegistrationButtonCancel = createCancelHandler(onRegistrationButtonClose)
  const handleRegistrationButtonSubmit = createSubmitHandler(onRegistrationButtonClose)

  const handleSpeakerFormChange = (values: SpeakerFormValues) => {
    updateSpeakerPreview(editingSpeaker, tempSpeakerIdRef, values)
  }

  return (
    <>
      <EventPage
        header={(
          <EventPageHeader
            icon="website"
            title="Web Page"
            items={(
              <>
                <ViewsCount label="Web Page Views" count={viewsCount} />
                <HStack spacing={3}>
                  <PageLinkSelector website={website} redirectPath={eventWebPagePath(eventId)} />
                  <PublishedUpdater event={event} />
                </HStack>
              </>
            )}
          />
        )}
        body={(
          <Flex
            p={12}
            w="100%"
            minH="100%"
            bgColor="gray.100"
          >
            <Box w="100%" maxW="1400px" mx="auto" position="relative" alignSelf="flex-start">
              <Box
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                w="80%"
                maxW="1140px"
                borderRadius="lg"
                boxShadow="lg"
                pointerEvents="none"
              />
              <Flex position="relative" align="flex-start" justifyContent="space-between" w="100%">
                <Box w="80%" maxW="1140px">
                  <TopBar variant="lg" />
                </Box>
              </Flex>
              <WebsitePreviewSection
                menuTitle="Hero"
                menuIcon="site-homepage"
                menuOptions={[
                  { label: "Hero Template", onClick: onHeroTemplateOpen },
                  { label: "Hero Image", onClick: onHeroImageOpen },
                  { type: "divider" },
                  { label: "Page Header", onClick: onPageHeaderOpen },
                  { label: "Registration Button", onClick: onRegistrationButtonOpen },
                ]}
                noPadding
              >
                <SiteCover
                  website={previewWebsite}
                  buttonClick={handleRegistrationButtonClick}
                />
              </WebsitePreviewSection>
              <WebsitePreviewSection
                menuTitle="Content"
                menuIcon="page-outline"
                menuOptions={[
                  { label: "Content Builder", onClick: onWebpageBuilderOpen },
                  { type: "divider" },
                  { label: "Sidebar", onClick: onSidebarOpen },
                ]}
              >
                <EventDetail website={previewWebsite} websiteBlocks={websiteBlocks} />
              </WebsitePreviewSection>
              <WebsitePreviewSection
                menuTitle="Speakers"
                menuIcon="speakers"
                menuMt={10}
                menuOptions={[
                  { label: "Speaker Settings", onClick: onSpeakerSettingsOpen },
                  { type: "divider" },
                  { label: "Add Speaker", onClick: onAddSpeakerOpen },
                ]}
              >
                <Speakers
                  speakers={previewWebsite.speakers}
                  website={previewWebsite}
                  onEditSpeaker={handleEditSpeaker}
                  onAddSpeaker={onAddSpeakerOpen}
                />
              </WebsitePreviewSection>
              <WebsitePreviewSection
                menuTitle="Agenda"
                menuIcon="agenda"
                menuMt={10}
                menuOptions={[
                  { label: "Agenda Settings", onClick: onAgendaSettingsOpen },
                  { type: "divider" },
                  { label: "Add Group", onClick: onAddAgendaGroupOpen },
                  { label: "Add Entry", onClick: onAddAgendaEntryOpen },
                ]}
              >
                <Agenda
                  agendaRows={previewWebsite.agendaRows}
                  speakers={website.speakers}
                  website={previewWebsite}
                  onEditAgendaItem={handleEditAgendaItem}
                  onEditAgendaGroup={handleEditAgendaGroup}
                  onAddFirstEntry={onAddAgendaEntryOpen}
                />
              </WebsitePreviewSection>
              <WebsitePreviewSection
                menuTitle="Map"
                menuIcon="map-outline"
                overflowHidden
                menuOptions={[
                  { label: "Map Settings", onClick: onMapSettingsOpen },
                ]}
                isLast={event.whiteLabel}
                noPadding
                minH={!event.whiteLabel ? 32 : undefined}
              >
                <GoogleMap
                  website={previewWebsite}
                />
              </WebsitePreviewSection>
              {!event.whiteLabel && (
              <WebsitePreviewSection isLast>
                <PoweredBy />
              </WebsitePreviewSection>
              )}
            </Box>
          </Flex>
      )}
      />
      {isHeroTemplateOpen && (
        <HeroTemplate
          eventId={eventId}
          website={website}
          onClose={handleHeroTemplateCancel}
          onHide={handleHeroTemplateSubmit}
          onChange={handleHeroTemplateChange}
        />
      )}
      {isHeroImageOpen && (
        <HeroImage
          eventId={eventId}
          coverImageUrl={website.coverImageUrl}
          onClose={handleHeroImageCancel}
          onHide={handleHeroImageSubmit}
          onChange={handleHeroImageChange}
        />
      )}
      {isPageHeaderOpen && (
        <PageHeader
          eventId={eventId}
          title={website.title}
          onClose={handlePageHeaderCancel}
          onHide={handlePageHeaderSubmit}
          onChange={handlePageHeaderChange}
        />
      )}
      {isRegistrationButtonOpen && (
      <ActionButton
        eventId={eventId}
        event={event}
        website={website}
        onClose={handleRegistrationButtonCancel}
        onHide={handleRegistrationButtonSubmit}
        onChange={handleRegistrationButtonChange}
      />
      )}
      {isWebpageBuilderOpen && (
        <WebpageBuilder
          eventId={eventId}
          website={website}
          onHide={onWebpageBuilderClose}
        />
      )}
      {isSidebarOpen && (
        <Sidebar
          eventId={eventId}
          website={website}
          onClose={handleSidebarCancel}
          onHide={handleSidebarSubmit}
          onChange={handleSidebarChange}
        />
      )}
      {isSpeakerSettingsOpen && (
        <SpeakerSettings
          eventId={eventId}
          website={website}
          onClose={handleSpeakerSettingsCancel}
          onHide={handleSpeakerSettingsSubmit}
          onChange={handleSpeakerSettingsChange}
        />
      )}
      {(isAddSpeakerOpen || editingSpeaker) && (
        <SpeakerForm
          speaker={editingSpeaker || undefined}
          onHide={handleSpeakerFormClose}
          onChange={handleSpeakerFormChange}
        />
      )}
      {isAgendaSettingsOpen && (
        <AgendaSettings
          eventId={eventId}
          website={website}
          onClose={handleAgendaSettingsCancel}
          onHide={handleAgendaSettingsSubmit}
          onChange={handleAgendaSettingsChange}
        />
      )}
      {(isAddAgendaGroupOpen || editingAgendaGroup) && (
        <AddAgendaGroup
          eventId={eventId}
          onHide={handleAgendaGroupClose}
          onChange={handleAgendaGroupChange}
          agendaGroup={editingAgendaGroup || undefined}
          eventStartDate={event.startDate}
          eventTimezone={event.timezone}
          agendaRows={website.agendaRows}
        />
      )}
      {(isAddAgendaEntryOpen || editingAgendaItem) && (
        <AddAgendaEntry
          eventId={eventId}
          speakers={website.speakers}
          onHide={handleAgendaItemFormClose}
          agendaItem={editingAgendaItem || undefined}
          onChange={handleAgendaEntryChange}
          eventStartDate={event.startDate}
          eventTimezone={event.timezone}
          agendaRows={website.agendaRows}
        />
      )}
      {isMapSettingsOpen && (
        <MapSettings
          eventId={eventId}
          website={website}
          onClose={handleMapSettingsCancel}
          onHide={handleMapSettingsSubmit}
          onChange={handleMapSettingsChange}
        />
      )}
    </>
  )
}

export default WebPageSettings
